import { getTimezoneOffset } from 'date-fns-tz';
import { getTimeZone } from 'react-native-localize';
import { AppsyncResult } from '../apis/appsyncHelper';
import { MarkerGJFeature } from '../components/map/content/MarkerCluster';
import {
  Maybe,
  PodStatus,
  PoisType,
  ProductSubscription,
  ReservationStatus,
  ReservationType,
} from '../types/appsync-types';
import { Pod } from '../types/misc.types';

export type GeoJsonWithReservation = {
  type: 'space';
  spaceType?: PoisType | null;
  title: string;
  lon: number;
  lat: number;
  localRef: string;
  buildingRef: string;
  poiId: string;
  poiParentId: string | null;
  layerIndex: number;
  reservationStatus: ReservationStatus | null;
  reservationType: ReservationType | null;
  podStatus: PodStatus;
  podTzOffsetMs: number;
  nextFreeStart: number; // Unix timestamp
  currentFreeEnd: number; // Unix timestamp
  selected: boolean;
};

export type GeoJsonWithVenueAvailability = {
  type: 'venue';
  available: number;
  total: number;
  podTzOffsetMs: number;
  nextFreeStart: number; // Unix timestamp
  currentFreeEnd: number; // Unix timestamp
  buildingRef: string;
  title: string;
};

export function convertGetBuildingResultToGeoJson(
  result: NonNullable<AppsyncResult<'getBuildingsWithinRadius'>['data']>,
  unpaidAndUpcomingReservations: Maybe<ProductSubscription>[],
  buildingNameTranslator: (title: string | null | undefined) => string,
): {
  venues: MarkerGJFeature<GeoJsonWithVenueAvailability>[];
  pois: MarkerGJFeature<GeoJsonWithReservation>[];
  services: MarkerGJFeature<GeoJsonWithReservation>[];
} {
  const pods =
    result.getBuildingsWithinRadius
      /* Flatten to list of spaces */
      ?.reduce((accumulator, building) => {
        building?.spaces?.forEach((s) => (s ? accumulator.push(s) : null));
        return accumulator;
      }, [] as Pod[])
      .filter(
        (poi) =>
          poi &&
          poi.localRef &&
          poi.buildingRef &&
          !Number.isNaN(poi.lon) &&
          !Number.isNaN(poi.lat) &&
          poi.availabilityStatus?.status,
      )
      /* Convert to geojson */
      .reduce(
        (accumulator, poi, index) => {
          const isFree =
            poi.availabilityStatus?.status === PodStatus.FREE ||
            poi.availabilityStatus?.status === PodStatus.RESERVED_BY_ME;
          const avStat = poi.availabilityStatus!;
          let currentFreeEnd = 0;
          let nextFreeStart = 0;
          if (isFree) {
            currentFreeEnd = new Date(avStat.nextFreeEnd!).getTime() / 1000;
          } else {
            nextFreeStart = new Date(avStat.nextFreeStart!).getTime() / 1000;
          }

          // We only take the first letter of the title as a workaround for this issue
          // https://github.com/biketti/biketti-pod-app/issues/287
          // TODO: Figure out better way to handle map short name vs full title
          // const title = (poi?.title ?? 'S').charAt(0);
          const title = poi?.title ?? 'Biketti';
          const podToUtc = getTimezoneOffset(poi.building?.timezone ?? getTimeZone(), new Date(nextFreeStart));
          const viewToUtc = getTimezoneOffset(getTimeZone(), new Date(nextFreeStart));
          const podTzOffsetMs = podToUtc - viewToUtc;

          let availabilityStatus = poi.availabilityStatus?.status!;

          if (
            unpaidAndUpcomingReservations.some((reservation) =>
              reservation?.product?.linkedPods?.some((linkedPod) => linkedPod?.localRef === poi.localRef),
            )
          ) {
            // TODO: Maybe add an 'unpaid' status? And also this logic might be moved to backend so delete this if/when that happens
            availabilityStatus = PodStatus.RESERVED_BY_ME;
          }

          const v: MarkerGJFeature<GeoJsonWithReservation> = {
            type: 'Feature',
            id: index,
            properties: {
              type: 'space',
              spaceType: poi.spaceType,
              poiId: poi?.id,
              poiParentId: poi.parentRef ?? null,
              title,
              localRef: poi!.localRef!,
              layerIndex: Number.parseInt(poi?.layerIndex ?? '0', 10),
              buildingRef: poi!.buildingRef!,
              lon: poi!.lon!,
              lat: poi!.lat!,
              currentFreeEnd,
              podTzOffsetMs,
              nextFreeStart,
              // The status will be yielded by the reservation subscription
              reservationStatus: null,
              reservationType: null,
              podStatus: availabilityStatus,
              // The selection is managed by MapView
              selected: false,
            },
            geometry: {
              type: 'Point',
              coordinates: [poi.lon!, poi.lat!],
            },
          };
          let key: 'pois' | 'lockerGroups' | 'lockers' | null;
          switch (poi.spaceType) {
            case PoisType.PARKING_AREA:
              key = 'pois';
              break;
            case PoisType.LOCKER_GROUP:
              key = 'lockerGroups';
              break;
            case PoisType.LOCKER:
              key = 'lockers';
              break;
            default:
              key = null;
          }
          if (!key) return accumulator;
          return { ...accumulator, [key]: [...accumulator[key], v] };
        },
        { pois: [], lockerGroups: [], lockers: [] },
      ) ??
    ({ pois: [], lockerGroups: [], lockers: [] } as {
      pois: MarkerGJFeature<GeoJsonWithReservation>[];
      lockerGroups: MarkerGJFeature<GeoJsonWithReservation>[];
      lockers: MarkerGJFeature<GeoJsonWithReservation>[];
    });

  const res = {
    venues:
      result.getBuildingsWithinRadius
        ?.filter((b) => b && b.spaces && b.buildingRef && !Number.isNaN(b.lon) && !Number.isNaN(b.lat))
        ?.map((building, index) => {
          const spaces =
            building?.spaces?.filter(
              (s) => s && s.spaceType === PoisType.PARKING_AREA && s.availabilityStatus?.status,
            ) ?? [];
          const availableCount = spaces.reduce(
            (acc, s) =>
              s!.availabilityStatus!.status === PodStatus.FREE ||
              s!.availabilityStatus!.status === PodStatus.RESERVED_BY_ME
                ? acc + 1
                : acc,
            0,
          );
          let lastFreeEnd: number = 0;
          let firstFreeStart: number = 0;
          if (availableCount) {
            /* Find longest possible availability */
            lastFreeEnd = spaces.reduce(
              (lastFree, s) =>
                s!.availabilityStatus!.status === PodStatus.FREE ||
                s!.availabilityStatus!.status === PodStatus.RESERVED_BY_ME
                  ? Math.max(lastFree, new Date(s!.availabilityStatus!.nextFreeEnd!).getTime() / 1000)
                  : lastFree,
              -1,
            );
          } else {
            /* Find first possible availability */
            firstFreeStart = spaces.reduce(
              (firstFree, s) => Math.min(firstFree, new Date(s!.availabilityStatus!.nextFreeStart!).getTime() / 1000),
              Infinity,
            );
          }
          const podToUtc = getTimezoneOffset(building?.timezone ?? getTimeZone(), new Date(firstFreeStart));
          const viewToUtc = getTimezoneOffset(getTimeZone(), new Date(firstFreeStart));
          const podTzOffsetMs = podToUtc - viewToUtc;
          const v: MarkerGJFeature<GeoJsonWithVenueAvailability> = {
            type: 'Feature',
            id: index + 1,
            properties: {
              type: 'venue',
              total: spaces.length ?? 0,
              available: availableCount,
              podTzOffsetMs,
              nextFreeStart: firstFreeStart,
              currentFreeEnd: lastFreeEnd,
              buildingRef: building!.buildingRef!,
              title: buildingNameTranslator(building!.title),
            },
            geometry: {
              type: 'Point',
              coordinates: [building!.lon!, building!.lat!],
            },
          };
          return v;
        })
        ?.filter((x) => !!x) ?? [],
    pois: pods.pois,
    services: pods.lockerGroups.map((lockerGroup) => ({
      ...lockerGroup,
      properties: {
        ...lockerGroup.properties,
        // TODO: This is combining the statuses of the lockers within a locker group, this should preferably happen in the backend
        ...combinedPodByStatus(
          pods.lockers.filter((locker) => locker.properties.poiParentId === lockerGroup.properties.localRef),
        ),
      },
    })),
  };
  return res;
}

function combineStatus(overallStatus: PodStatus, currStatus: PodStatus) {
  // A pod is booked by the user, no need to update status
  if (overallStatus === PodStatus.RESERVED_BY_ME) return overallStatus;
  // Current pod is booked by the user and none so far are, update status
  if (currStatus === PodStatus.RESERVED_BY_ME) return currStatus;
  // At least one pod is available and none so far are booked by user, no need to update status
  if (overallStatus === PodStatus.FREE) return overallStatus;
  // Current pod is available and none so far are booked by user or available, update status
  if (currStatus === PodStatus.FREE) return currStatus;
  // At least one pod is subscription only and none so far are available or booked by user, no need to update status
  if (overallStatus === PodStatus.SUBSCRIPTION_ONLY) return overallStatus;
  // Current pod is subscription only and none so far are available, booked by user, or subscription only, update status
  if (currStatus === PodStatus.SUBSCRIPTION_ONLY) return currStatus;
  // At least one pod is already reserved and none so far are available or booked by user, no need to update status
  if (overallStatus === PodStatus.RESERVED || overallStatus === PodStatus.RESERVED_BY_OTHER) return overallStatus;
  // Current pod is already reserved and none so far are available, booked by user, or already reserved, update status
  if (currStatus === PodStatus.RESERVED || currStatus === PodStatus.RESERVED_BY_OTHER) return currStatus;
  return overallStatus;
}

type CombinedPodProperties = Pick<
  GeoJsonWithReservation,
  'podStatus' | 'currentFreeEnd' | 'podTzOffsetMs' | 'nextFreeStart'
>;
function combinedPodByStatus(pods: MarkerGJFeature<GeoJsonWithReservation>[]): CombinedPodProperties {
  return pods.reduce((combinedPod, pod) => {
    const podStatus = combineStatus(combinedPod.podStatus, pod.properties.podStatus);
    if (combinedPod.podStatus !== podStatus) {
      return {
        podStatus,
        currentFreeEnd: pod.properties.currentFreeEnd,
        podTzOffsetMs: pod.properties.podTzOffsetMs,
        nextFreeStart: pod.properties.nextFreeStart,
      };
    }
    return combinedPod;
  }, {} as CombinedPodProperties);
}
